import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    ChalkboardTeacher,
    Sparkle,
    CheckCircle,
    ArrowRight,
    Question,
    Lightbulb,
    ArrowsClockwise,
    Clock,
    Coin,
    SealCheck,
} from "@phosphor-icons/react";

const INR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

const FOCUS_OPTIONS = [
    { key: "mixed",        label: "Balanced mix" },
    { key: "behavioral",   label: "Behavioral (STAR)" },
    { key: "technical",    label: "Technical / role-specific" },
    { key: "system-design", label: "System design" },
];

export default function CandidateInterviewPrep() {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [buyingPack, setBuyingPack] = useState(null);

    // Generator form
    const [role, setRole] = useState("");
    const [years, setYears] = useState("");
    const [focus, setFocus] = useState("mixed");
    const [jdText, setJdText] = useState("");
    const [session, setSession] = useState(null); // freshly generated Qs
    const [pastSessions, setPastSessions] = useState([]);

    const loadStatus = useCallback(async () => {
        try {
            const r = await api.get("/me/interview-prep/status");
            setStatus(r.data);
        } catch {
            setStatus({ credits: 0, pricing: {} });
        }
    }, []);

    const loadSessions = useCallback(async () => {
        try {
            const r = await api.get("/me/interview-prep/sessions", { params: { limit: 10 } });
            setPastSessions(r.data.items || []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        loadStatus();
        loadSessions();
    }, [loadStatus, loadSessions]);

    const buy = async (pack) => {
        setBuyingPack(pack);
        try {
            const rzOk = await new Promise((resolve) => {
                if (window.Razorpay) return resolve(true);
                const s = document.createElement("script");
                s.src = "https://checkout.razorpay.com/v1/checkout.js";
                s.onload = () => resolve(true);
                s.onerror = () => resolve(false);
                document.body.appendChild(s);
            });
            if (!rzOk) throw new Error("Couldn't load payment gateway");
            const { data: order } = await api.post("/me/interview-prep/checkout", { pack });
            const rz = new window.Razorpay({
                key: order.key_id,
                amount: order.amount_paise,
                currency: order.currency,
                order_id: order.order_id,
                name: "NaukriDaddy",
                description: pack === "single" ? "Interview Prep · 1 session" : "Interview Prep · 5 sessions",
                theme: { color: "#1E3AEF" },
                handler: async (rp) => {
                    try {
                        const v = await api.post("/me/interview-prep/verify", {
                            razorpay_order_id: rp.razorpay_order_id,
                            razorpay_payment_id: rp.razorpay_payment_id,
                            razorpay_signature: rp.razorpay_signature,
                        });
                        toast.success(`+${v.data.added || 0} credits added.`);
                        loadStatus();
                    } catch {
                        toast.error("Payment verification failed");
                    }
                },
                modal: { ondismiss: () => setBuyingPack(null) },
            });
            rz.open();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start checkout");
        } finally { setBuyingPack(null); }
    };

    const generate = async (e) => {
        e?.preventDefault();
        if (!role.trim()) { toast.error("Enter a role first"); return; }
        setBusy(true);
        setSession(null);
        try {
            const r = await api.post("/me/interview-prep/generate", {
                role: role.trim(),
                years: Number(years) || 0,
                focus,
                jd_text: jdText.trim(),
            });
            setSession(r.data);
            toast.success(`${r.data.questions.length} questions ready · ${r.data.credits_remaining} credit${r.data.credits_remaining === 1 ? "" : "s"} left`);
            loadStatus();
            loadSessions();
        } catch (err) {
            const s = err?.response?.status;
            const d = err?.response?.data?.detail;
            if (s === 402) {
                toast.error(typeof d === "object" ? d.message : "Out of credits — buy more below.");
            } else {
                toast.error(typeof d === "object" ? d.message : (d || "Generation failed"));
            }
        } finally { setBusy(false); }
    };

    const openSession = async (sid) => {
        try {
            const r = await api.get(`/me/interview-prep/sessions/${sid}`);
            setSession({
                session_id: r.data.id,
                role: r.data.role,
                focus: r.data.focus,
                questions: r.data.questions,
                credits_remaining: status?.credits ?? 0,
                interview_ready: status?.interview_ready,
            });
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            toast.error("Couldn't load session");
        }
    };

    if (!status) return <div className="p-10 text-[#525252]">Loading…</div>;

    const credits = status.credits || 0;
    const single = status.pricing?.single || {};
    const pack5 = status.pricing?.pack5 || {};

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="interview-prep-page">
            {/* Hero */}
            <div className="mb-8">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-2 inline-flex items-center gap-1.5">
                    <ChalkboardTeacher size={11} weight="fill" /> AI Interview Coach
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">
                    Show up to your interview <span className="text-cobalt">ready</span>.
                </h1>
                <p className="text-sm sm:text-base text-[#525252] mt-3 max-w-2xl">
                    Get <b>8 personalised questions</b> based on your resume and the JD you paste,
                    with STAR-format model answers and expected follow-ups. Every session earns
                    you the &quot;Interview-ready&quot; badge in the recruiter Marketplace.
                </p>
            </div>

            {/* Credits + Buy */}
            <div className="grid gap-4 sm:grid-cols-3 mb-8">
                <div data-testid="ip-credits-card"
                     className="bg-ink text-white p-5 flex flex-col justify-between">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-splash inline-flex items-center gap-1">
                            <Coin size={11} weight="fill" /> Your credits
                        </p>
                        <p className="font-heading text-5xl font-black tracking-tighter mt-2">{credits}</p>
                    </div>
                    <p className="text-[11px] text-white/70 mt-3">
                        1 credit = 1 mock interview · 8 tailored questions
                    </p>
                </div>

                <PricingCard
                    testid="ip-buy-single"
                    label="1 session"
                    price={single.price_inr}
                    credits={single.credits}
                    perSession={single.price_inr}
                    onBuy={() => buy("single")}
                    busy={buyingPack === "single"}
                />
                <PricingCard
                    testid="ip-buy-pack5"
                    label="5-session pack"
                    price={pack5.price_inr}
                    credits={pack5.credits}
                    perSession={pack5.price_inr / (pack5.credits || 1)}
                    savings={pack5.savings_inr > 0 ? pack5.savings_inr : null}
                    highlight
                    onBuy={() => buy("pack5")}
                    busy={buyingPack === "pack5"}
                />
            </div>

            {status.interview_ready && (
                <div data-testid="ip-ready-banner"
                     className="mb-6 bg-emerald-50 border border-emerald-300 p-4 flex items-start gap-3">
                    <SealCheck size={22} weight="fill" className="text-emerald-600 mt-0.5" />
                    <div>
                        <p className="font-heading font-black text-emerald-800">You&apos;re Interview-ready.</p>
                        <p className="text-xs text-emerald-800/80 mt-0.5">
                            Recruiters can now filter for you in the Talent Marketplace using the &quot;Interview-ready&quot; badge.
                            {status.total_sessions} session{status.total_sessions === 1 ? "" : "s"} completed.
                        </p>
                    </div>
                </div>
            )}

            {/* Generator */}
            <form onSubmit={generate} data-testid="ip-generator-form"
                  className="bg-white border border-[#E5E5E5] p-5 sm:p-6 mb-8 space-y-4">
                <h2 className="font-heading text-xl font-black tracking-tight">New session</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1">Role you&apos;re interviewing for</label>
                        <input
                            data-testid="ip-role-input"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            placeholder="Backend Engineer"
                            className="w-full px-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1">Years of experience</label>
                        <input
                            data-testid="ip-years-input"
                            type="number"
                            min="0"
                            max="40"
                            value={years}
                            onChange={(e) => setYears(e.target.value)}
                            placeholder="5"
                            className="w-full px-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">Focus</label>
                    <div className="flex flex-wrap gap-2">
                        {FOCUS_OPTIONS.map((f) => (
                            <button key={f.key} type="button"
                                    data-testid={`ip-focus-${f.key}`}
                                    onClick={() => setFocus(f.key)}
                                    className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                                        focus === f.key ? "bg-ink text-white border-ink" : "border-[#E5E5E5] hover:border-ink"
                                    }`}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1">Job description (optional but recommended)</label>
                    <textarea
                        data-testid="ip-jd-input"
                        value={jdText}
                        onChange={(e) => setJdText(e.target.value)}
                        rows={4}
                        placeholder="Paste the JD here — questions will be tailored to what THIS company is asking."
                        className="w-full px-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink resize-none"
                    />
                </div>
                <button type="submit" disabled={busy || credits < 1}
                        data-testid="ip-generate-btn"
                        className="btn-gradient w-full sm:w-auto px-6 py-3 text-[11px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {busy ? (<><ArrowsClockwise size={12} weight="bold" className="animate-spin" /> NaukriDaddy AI is thinking…</>) :
                        credits < 1 ? "Buy credits to generate" : (<>Generate my session <ArrowRight size={12} weight="bold" /></>)}
                </button>
            </form>

            {/* Session output */}
            {session && (
                <div data-testid="ip-session-output" className="mb-8">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                        <h2 className="font-heading text-2xl font-black tracking-tight">
                            {session.role} · {session.focus.replace("-", " ")}
                        </h2>
                        <span className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                            {session.questions.length} questions
                        </span>
                    </div>
                    <div className="space-y-4">
                        {session.questions.map((q, i) => (
                            <QuestionCard key={i} idx={i + 1} q={q} />
                        ))}
                    </div>
                </div>
            )}

            {/* Past sessions */}
            {pastSessions.length > 0 && (
                <div data-testid="ip-history" className="mt-8">
                    <h2 className="font-heading text-xl font-black tracking-tight mb-3">Past sessions</h2>
                    <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                        {pastSessions.map((s) => (
                            <button key={s.id} onClick={() => openSession(s.id)}
                                    data-testid={`ip-past-${s.id}`}
                                    className="w-full text-left p-3 flex items-center justify-between hover:bg-[#FAFAFA]">
                                <div className="min-w-0">
                                    <p className="font-heading text-sm font-bold text-ink truncate">{s.role}</p>
                                    <p className="text-[10px] text-[#A3A3A3] mt-0.5 flex items-center gap-2">
                                        <Clock size={10} weight="fill" />
                                        {new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                                        · {s.focus.replace("-", " ")}
                                        {s.jd_provided && " · with JD"}
                                    </p>
                                </div>
                                <ArrowRight size={12} weight="bold" className="text-[#A3A3A3]" />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- Pricing card ----------
function PricingCard({ testid, label, price, credits, perSession, savings, highlight, onBuy, busy }) {
    return (
        <div data-testid={testid}
             className={`p-5 border flex flex-col ${
                 highlight ? "bg-white border-splash shadow-[0_0_0_2px_rgba(91,226,235,0.35)]" : "bg-white border-[#E5E5E5]"
             }`}>
            <p className="text-[10px] font-black uppercase tracking-wider text-cobalt">
                {label}
            </p>
            <p className="font-heading text-3xl font-black tracking-tighter mt-2">{INR(price)}</p>
            <p className="text-[11px] text-[#A3A3A3] mt-1">
                {INR(Math.round(perSession))} per session · {credits} credit{credits === 1 ? "" : "s"}
            </p>
            {savings > 0 && (
                <p className="text-[10px] font-black text-emerald-600 mt-1">Save {INR(savings)}</p>
            )}
            <button onClick={onBuy} disabled={busy}
                    className={`mt-4 py-2.5 text-[10px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1 ${
                        highlight ? "btn-gradient text-ink" : "bg-ink text-white hover:bg-cobalt"
                    } ${busy ? "opacity-60" : ""}`}>
                {busy ? "Opening…" : (<>Buy <ArrowRight size={12} weight="bold" /></>)}
            </button>
        </div>
    );
}

// ---------- Individual question card ----------
function QuestionCard({ idx, q }) {
    const [expanded, setExpanded] = useState(false);
    const catColor = q.category === "technical" ? "bg-cobalt/10 text-cobalt border-cobalt/30"
        : q.category === "behavioral" ? "bg-splash/20 text-ink border-splash/40"
        : q.category === "system-design" ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]";
    return (
        <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5">
            <div className="flex items-start gap-3">
                <span className="font-heading text-2xl font-black text-[#E5E5E5] flex-shrink-0">
                    {String(idx).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 border ${catColor}`}>
                            {q.category}
                        </span>
                    </div>
                    <p className="font-heading text-base font-bold text-ink leading-snug">
                        <Question size={14} weight="fill" className="inline text-cobalt mr-1 -mt-1" />
                        {q.q}
                    </p>
                    {q.why_asked && (
                        <p className="text-[11px] text-[#525252] italic mt-1.5">
                            {q.why_asked}
                        </p>
                    )}
                    <button onClick={() => setExpanded((v) => !v)}
                            className="mt-3 text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-ink inline-flex items-center gap-1">
                        <Lightbulb size={11} weight="fill" /> {expanded ? "Hide model answer" : "Show model answer"}
                    </button>
                    {expanded && (
                        <div className="mt-3 space-y-3">
                            <div className="bg-[#FAFAFA] border-l-2 border-splash p-3">
                                <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Star template</p>
                                <p className="text-xs text-ink leading-relaxed">{q.star_answer_template}</p>
                            </div>
                            {q.follow_ups?.length > 0 && (
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Expected follow-ups</p>
                                    <ul className="space-y-1">
                                        {q.follow_ups.map((f, i) => (
                                            <li key={i} className="text-xs text-[#404040] flex items-start gap-2">
                                                <CheckCircle size={11} weight="fill" className="text-splash mt-0.5 flex-shrink-0" />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
